
import { useNavigate } from 'react-router-dom';

export default function ServicesSection() {
  const navigate = useNavigate();

  const services = [
    {
      icon: 'ri-home-heart-line',
      title: 'Inherited Properties',
      description: 'Inherited a property you don\'t want or can\'t maintain? We simplify the process of selling inherited homes, handling all the complexities while you focus on what matters.',
      features: [
        'No probate delays',
        'Handle estate settlements',
        'Clear title issues',
        'Remote closing available'
      ],
      link: '/inherited-homes'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Foreclosure Prevention',
      description: 'Facing foreclosure? We can help you avoid it by purchasing your home quickly, protecting your credit score and giving you a fresh financial start.',
      features: [
        'Stop foreclosure fast',
        'Protect your credit',
        'Avoid deficiency judgments',
        'Get cash to relocate'
      ],
      link: '/avoid-foreclosure'
    },
    {
      icon: 'ri-home-heart-fill',
      title: 'Distressed Properties',
      description: 'We buy houses in any condition - from pristine to distressed. No repairs needed, no cleaning required. Sell your home exactly as-is and save thousands on renovation costs.',
      features: ['Fire or water damage', 'Foundation issues', 'Outdated interiors', 'Hoarder situations'],
      link: '/sell-distressed-property'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Title Issues',
      description: 'Have liens, clouded titles, or probate complications? We specialize in buying properties with title problems that other buyers won\'t touch.',
      features: ['Clouded titles', 'Tax liens', 'Probate issues', 'Boundary disputes'],
      link: '/title-issues'
    },
    {
      icon: 'ri-building-fill',
      title: 'Rental Properties',
      description: 'Tired of being a landlord? Dealing with problem tenants? We buy rental properties with tenants in place, saving you the hassle of evictions and property management.',
      features: ['Buy with tenants', 'No eviction needed', 'Handle all paperwork', 'Quick closings'],
      link: '/rental-property'
    },
    {
      icon: 'ri-landscape-line',
      title: 'Vacant Land',
      description: 'Own vacant land you can\'t sell? We buy all types of land - raw, undeveloped, inherited, or with tax liens. Turn your unused property into cash.',
      features: ['Any size parcels', 'No utilities needed', 'Tax lien properties', 'Inherited land'],
      link: '/land-buyers'
    },
    {
      icon: 'ri-exchange-dollar-fill',
      title: 'Divorce Property Sales',
      description: 'Going through a divorce? Selling your shared property quickly can help both parties move forward. We provide fair offers and handle everything discreetly and professionally.',
      features: ['Quick resolution', 'Fair market value', 'Confidential process', 'Split proceeds easily']
    },
    {
      icon: 'ri-map-pin-time-fill',
      title: 'Relocation Services',
      description: 'Need to relocate for work or family? We understand time is critical. Get your cash offer within 24 hours and close on your schedule - as fast as 7 days if needed.',
      features: ['Flexible closing dates', 'Fast cash offers', 'No waiting for buyers', 'Stress-free process']
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Abandoned Properties',
      description: 'Own an abandoned property that\'s become a burden? We buy vacant and abandoned homes regardless of condition, liens, or back taxes owed. Turn your liability into cash.',
      features: [
        'No cleanup required',
        'Handle code violations',
        'Clear back taxes',
        'Buy as-is condition'
      ]
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-6">
            Our Services
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
            We Buy Houses in <strong>Any Situation</strong>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            No matter what challenges you're facing with your property, we have the experience and resources to provide a solution that works for you. Learn more about <a href="/elpaso-tx" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">our El Paso services</a>.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              onClick={() => service.link && navigate(service.link)}
              className="bg-gray-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-teal-200 group cursor-pointer"
            >
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-teal-600 transition-colors">
                <i className={`${service.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
              
              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center space-x-2 text-sm text-gray-700">
                    <i className="ri-check-line text-teal-600"></i>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">Ready to Sell Your El Paso Home?</h3>
          <p className="text-xl mb-8 text-teal-50">Get your free, no-obligation cash offer today. Check out our <a href="/local-news-el-paso-texas" className="text-white underline hover:text-teal-100 cursor-pointer">latest El Paso news</a> for market insights.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#contact" className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-teal-600 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              <span>Get Cash Offer</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a href="tel:+1-915-206-5814" className="inline-flex items-center space-x-2 px-8 py-4 bg-teal-600 text-white rounded-lg font-bold text-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-phone-fill"></i>
              <span>Call (915) 206-5814</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
